import { create } from 'zustand';

// Light / dark theming. The actual palette lives in CSS (palette.css keyed by
// [data-theme="…"]); this store only flips the `data-theme` attribute on
// <html>, persists the choice, and re-themes the address-bar color. An inline
// script in index.html applies the same value before first paint to avoid a
// flash — so this store's job at runtime is just the toggle + persistence.
export type ThemeMode = 'dark' | 'light';

const STORAGE_KEY = 'dsr-theme';

function systemPref(): ThemeMode {
  const mq = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: light)') : null;
  return mq && mq.matches ? 'light' : 'dark';
}

function initialMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through */
  }
  return systemPref();
}

function applyMode(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', mode);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', mode === 'light' ? '#eef0f4' : '#090b11');
}

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: initialMode(),
  setMode: (mode) => {
    applyMode(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore persistence failure */
    }
    set({ mode });
  },
  toggle: () => get().setMode(get().mode === 'dark' ? 'light' : 'dark'),
}));
